'use client';

import { css } from '@bts-web/utils-style-engine';
import {
  TNotificationTriggerType,
  useAppNotification,
} from '../NotificationContextProvider';

const playItems: TNotificationTriggerType[] = [
  // Basic visual types
  {
    title: 'Success',
    visual: 'success',
  },
  {
    title: 'Error',
    visual: 'error',
  },
  {
    title: 'Warning',
    visual: 'warning',
  },
  {
    title: 'Info',
    visual: 'info',
  },
  {
    title: 'Neutral',
    visual: 'neutral',
  },

  // With subtitles
  {
    title: 'Success with subtitle',
    subtitle: 'Operation completed successfully',
    visual: 'success',
  },
  {
    title: 'Error with subtitle',
    subtitle: 'Something went wrong, please try again',
    visual: 'error',
  },
  {
    title: 'Info with subtitle',
    subtitle: 'This is additional information',
    visual: 'info',
  },

  // With close button
  {
    title: 'Success with close',
    visual: 'success',
    withClose: true,
  },
  {
    title: 'Error with close',
    visual: 'error',
    withClose: true,
  },
  {
    title: 'Info with close & subtitle',
    subtitle: 'You can close this manually',
    visual: 'info',
    withClose: true,
  },

  // Duration variations
  {
    title: 'Quick (1s)',
    visual: 'info',
    customDuration: 1000,
  },
  {
    title: 'Long (10s)',
    visual: 'warning',
    customDuration: 10000,
  },
  {
    title: 'Infinite duration',
    visual: 'neutral',
    customDuration: 'infinite',
    withClose: true,
  },
  {
    title: 'Infinite error (no close)',
    visual: 'error',
    customDuration: 'infinite',
  },

  // Position variations
  {
    title: 'Top Right',
    visual: 'info',
    position: 'topRight',
  },
  {
    title: 'Bottom Right',
    visual: 'success',
    position: 'bottomRight',
  },

  // Type variations
  {
    title: 'Toast Type',
    visual: 'info',
    type: 'toast',
  },
  {
    title: 'Global Type',
    visual: 'warning',
    type: 'global',
  },
  {
    title: 'Global with close',
    visual: 'error',
    type: 'global',
    withClose: true,
  },

  // Position + Type combinations
  {
    title: 'Top + Global',
    visual: 'success',
    position: 'topRight',
    type: 'global',
  },
  {
    title: 'Bottom + Global',
    visual: 'warning',
    position: 'bottomRight',
    type: 'global',
  },
  {
    title: 'Top + Toast',
    visual: 'info',
    position: 'topRight',
    type: 'toast',
  },
  {
    title: 'Bottom + Toast',
    visual: 'neutral',
    position: 'bottomRight',
    type: 'toast',
  },

  // With actions
  {
    title: 'With action button',
    subtitle: 'Click the action below',
    visual: 'info',
    withClose: true,
    action: {
      component: (
        <button
          style={{
            padding: '4px 8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => alert('Action clicked!')}
        >
          Action
        </button>
      ),
      altText: 'Perform action',
    },
  },
  {
    title: 'Action + Global',
    subtitle: 'Global notification with action',
    visual: 'warning',
    type: 'global',
    action: {
      component: (
        <button
          style={{
            padding: '6px 12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => alert('Global action!')}
        >
          Retry
        </button>
      ),
      altText: 'Retry operation',
    },
  },

  // Complex combinations
  {
    title: 'Kitchen Sink',
    subtitle:
      'All features enabled: subtitle, close, action, infinite duration',
    visual: 'success',
    position: 'topRight',
    type: 'toast',
    withClose: true,
    customDuration: 'infinite',
    action: {
      component: (
        <a
          href="#"
          style={{
            color: '#007bff',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.preventDefault();

            alert('Learn more clicked!');
          }}
        >
          Learn More
        </a>
      ),
      altText: 'Learn more about this feature',
    },
  },
  {
    title: 'Global Kitchen Sink',
    subtitle: 'Global version with all features',
    visual: 'error',
    position: 'bottomRight',
    type: 'global',
    withClose: true,
    customDuration: 'infinite',
    action: {
      component: (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{
              padding: '4px 8px',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: '#dc3545',
            }}
            onClick={() => alert('Dismissed!')}
          >
            Dismiss
          </button>
          <button
            style={{
              padding: '4px 8px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={() => alert('Fixed!')}
          >
            Fix Now
          </button>
        </div>
      ),
      altText: 'Action buttons',
    },
  },

  // Edge cases
  {
    title: '',
    subtitle: 'No title, only subtitle',
    visual: 'info',
  },
  {
    title:
      'Very long title that might wrap to multiple lines and test how the notification handles overflow',
    subtitle:
      'And a very long subtitle that also might wrap and we need to see how this looks in both mobile and desktop views with different notification types',
    visual: 'warning',
    withClose: true,
  },
  {
    title: 'Same ID Test 1',
    visual: 'info',
    id: 'duplicate-test',
  },
  {
    title: 'Same ID Test 2',
    visual: 'success',
    id: 'duplicate-test',
  },
  {
    title: 'Same ID Infinite',
    visual: 'error',
    customDuration: 'infinite',
    id: 'infinite-duplicate',
  },
  {
    title: 'Same ID Infinite 2',
    visual: 'warning',
    customDuration: 'infinite',
    id: 'infinite-duplicate',
  },
];

export const NotificationPlayGround = () => {
  const { setAppNotification, closeAllNotifications } = useAppNotification();

  return (
    <div>
      {/* Control buttons */}
      <div
        className={css({
          marginBottom: 'medium',
          display: 'flex',
          gap: 'small',
        })}
      >
        <button
          type="button"
          onClick={closeAllNotifications}
          className={css({
            padding: 'small',
            backgroundColor: 'negative.fill_primary',
            color: 'negative.text_on_fill',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
          })}
        >
          Clear All Notifications
        </button>
        <button
          type="button"
          onClick={() => {
            // Trigger multiple notifications at once
            playItems.slice(0, 5).forEach((item, index) => {
              setTimeout(
                () => setAppNotification({ ...item, id: `bulk-${index}` }),
                index * 200,
              );
            });
          }}
          className={css({
            padding: 'small',
            backgroundColor: 'information.fill_primary',
            color: 'information.text_on_fill',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          })}
        >
          Trigger Bulk Notifications
        </button>
      </div>

      {/* Notification buttons grid */}
      <ul
        className={css({
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(auto-fit, minmax(200px, 1fr))',
            md: 'repeat(auto-fit, minmax(250px, 1fr))',
          },
          gap: 'small',
          listStyle: 'none',
          padding: 0,
          margin: 0,
        })}
      >
        {playItems.map((item, index) => {
          const buttonId = `${item.title}-${index}`;

          return (
            <li key={buttonId}>
              <button
                type="button"
                onClick={() => setAppNotification({ ...item, id: buttonId })}
                className={css({
                  width: '100%',
                  textAlign: 'start',
                  border: '1px solid',
                  borderColor: 'neutrals.border_secondary',
                  borderRadius: '6px',
                  padding: 'small',
                  cursor: 'pointer',
                  backgroundColor: 'neutrals.fill_secondary',
                  '&:hover': {
                    backgroundColor: 'neutrals.fill_tertiary',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'extra_small',
                })}
              >
                <strong>{item.title || 'No Title'}</strong>
                <small className={css({ color: 'neutrals.text_secondary' })}>
                  {[
                    item.visual && `Visual: ${item.visual}`,
                    item.type && `Type: ${item.type}`,
                    item.position && `Position: ${item.position}`,
                    item.customDuration && `Duration: ${item.customDuration}`,
                    item.withClose && 'Closable',
                    item.action && 'Has Action',
                    item.subtitle && 'Has Subtitle',
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </small>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
